import { File, FileImage, FileSpreadsheet, FileText, Presentation } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getFileKind, FILE_KIND_LABEL, type FileKind } from '@/lib/file-kind'

interface FileTypeIconProps {
  mimeType?: string | null
  /** Used when the MIME type is missing or unhelpful (SharePoint, octet-stream). */
  fileName?: string | null
  /**
   * `badge` — the full page-with-colour-badge SVG. Legible from ~20px up; the
   * badge text is 19% of the icon height, so it turns to mush below that.
   * `glyph` — a tinted Lucide glyph for chips and other sub-16px surfaces,
   * where colour has to carry the distinction because text can't.
   */
  variant?: 'badge' | 'glyph'
  className?: string
  size?: number
}

// ===================
// Palette
// ===================

/**
 * Format brand colours, not theme tokens — a red PDF badge is an identifying
 * signal users already recognise from Windows/Office, not decoration. This is a
 * deliberate exception to the design system's "gray icons for metadata" rule;
 * the Library grid has shipped these colours since the component was written.
 */
const KIND_BADGE: Record<FileKind, { fill: string; text: string } | null> = {
  pdf: { fill: '#D93025', text: 'PDF' },
  word: { fill: '#2B579A', text: 'DOC' },
  excel: { fill: '#217346', text: 'XLS' },
  powerpoint: { fill: '#C43E1C', text: 'PPT' },
  csv: { fill: '#5F6B73', text: 'CSV' },
  text: { fill: '#5F6B73', text: 'TXT' },
  image: null,
  generic: null,
}

const KIND_GLYPH: Record<FileKind, { icon: LucideIcon; color: string }> = {
  pdf: { icon: FileText, color: '#D93025' },
  word: { icon: FileText, color: '#2B579A' },
  excel: { icon: FileSpreadsheet, color: '#217346' },
  powerpoint: { icon: Presentation, color: '#C43E1C' },
  csv: { icon: FileSpreadsheet, color: '#5F6B73' },
  text: { icon: FileText, color: '#5F6B73' },
  image: { icon: FileImage, color: '#5F6B73' },
  generic: { icon: File, color: 'currentColor' },
}

// ===================
// Badge artwork
// ===================

const PAGE_PATH = 'M5 2h9l5 5v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z'
const FOLD_PATH = 'M14 2v5h5'

function BadgedFile({
  kind,
  size,
  badge,
}: {
  kind: FileKind
  size: number
  badge: { fill: string; text: string }
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={FILE_KIND_LABEL[kind]}
    >
      <path d={PAGE_PATH} fill="#FFFFFF" stroke="#D4D9D4" strokeWidth="1" strokeLinejoin="round" />
      <path d={FOLD_PATH} fill="none" stroke="#D4D9D4" strokeWidth="1" strokeLinejoin="round" />
      <rect x="3" y="13" width="14" height="7" rx="1.5" fill={badge.fill} />
      <text
        x="10"
        y="18.3"
        textAnchor="middle"
        fontSize="4.6"
        fontWeight="700"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        fill="#FFFFFF"
        letterSpacing="0.1"
      >
        {badge.text}
      </text>
    </svg>
  )
}

function GenericFile({ size, tint = '#94A29B' }: { size: number; tint?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={FILE_KIND_LABEL.generic}
    >
      <path d={PAGE_PATH} fill="#FFFFFF" stroke={tint} strokeWidth="1.2" strokeLinejoin="round" />
      <path d={FOLD_PATH} fill="none" stroke={tint} strokeWidth="1.2" strokeLinejoin="round" />
      <line x1="6.5" y1="12" x2="14.5" y2="12" stroke={tint} strokeWidth="1.2" strokeLinecap="round" />
      <line x1="6.5" y1="15" x2="14.5" y2="15" stroke={tint} strokeWidth="1.2" strokeLinecap="round" />
      <line x1="6.5" y1="18" x2="11.5" y2="18" stroke={tint} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  )
}

function ImageFile({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={FILE_KIND_LABEL.image}
    >
      <path d={PAGE_PATH} fill="#FFFFFF" stroke="#D4D9D4" strokeWidth="1" strokeLinejoin="round" />
      <path d={FOLD_PATH} fill="none" stroke="#D4D9D4" strokeWidth="1" strokeLinejoin="round" />
      <rect x="5" y="11" width="12" height="8" rx="1" fill="#EEF3EF" />
      <circle cx="8" cy="14" r="1" fill="#94A29B" />
      <path d="M5 19l3.5-4 2.5 3 2-2 4 3" stroke="#94A29B" strokeWidth="1.1" fill="none" strokeLinejoin="round" />
    </svg>
  )
}

// ===================
// Component
// ===================

export function FileTypeIcon({
  mimeType,
  fileName,
  variant = 'badge',
  className,
  size = 20,
}: FileTypeIconProps) {
  const kind = getFileKind(mimeType, fileName)

  let icon: React.ReactNode
  if (variant === 'glyph') {
    const { icon: Glyph, color } = KIND_GLYPH[kind]
    icon = <Glyph size={size} color={color} role="img" aria-label={FILE_KIND_LABEL[kind]} />
  } else if (kind === 'image') {
    icon = <ImageFile size={size} />
  } else {
    const badge = KIND_BADGE[kind]
    icon = badge ? <BadgedFile kind={kind} size={size} badge={badge} /> : <GenericFile size={size} />
  }

  return <span className={cn('inline-flex shrink-0 items-center justify-center', className)}>{icon}</span>
}
